import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { List, AutoSizer, CellMeasurerCache, CellMeasurer } from 'react-virtualized';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverlay,
  DragOverEvent,
  UniqueIdentifier,
  DropAnimation,
  defaultDropAnimation,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';
import { CustomFieldGroup } from '../../../../../types/custom_fields';
import { Brand } from '../../../../../types/brand';
import { typingDone } from '../../../../utils/Utils';
import ParentCustomFieldTreeItem from './ParentCustomFieldTreeItem';
import SortableVirtualItem from './SortableVirtualItem';
import {
  flattenTree,
  buildTree,
  getProjection,
  removeChildrenOf,
  setProperty,
  FlattenedCustomFieldGroup,
} from '../../../../containers/settings/utilities';

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

interface ParentCustomFieldsVirtualListProps {
  groups: CustomFieldGroup[];
  keywords: string;
  brands: Brand[];
  onBrandChange?: (customFieldId: number, brandIds: number[]) => void;
  onEdit?: (id: number, type: string) => void;
  onGroupOrderChange?: (groups: CustomFieldGroup[]) => void;
  hasPendingOrderChanges?: boolean;
}

const ParentCustomFieldsVirtualList: React.FC<ParentCustomFieldsVirtualListProps> = ({
  groups,
  keywords,
  brands,
  onBrandChange,
  onEdit,
  onGroupOrderChange,
  hasPendingOrderChanges = false,
}) => {
  const [items, setItems] = useState<CustomFieldGroup[]>(groups);
  const [filteredItems, setFilteredItems] = useState<CustomFieldGroup[]>();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const listRef = useRef<List>(null);

  const brandOptions = useMemo(
    () => brands.map(brand => ({ label: brand.name, value: brand.id })),
    [brands]
  );

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Cache for measuring dynamic heights
  const cache = useMemo(
    () =>
      new CellMeasurerCache({
        fixedHeight: false,
        defaultHeight: 54,
        fixedWidth: true,
      }),
    []
  );

  const flattenedItems = useMemo(() => {
    const itemsToDisplay = filteredItems || items;
    const flattenedTree = flattenTree(itemsToDisplay);
    const collapsedItems = flattenedTree.reduce<UniqueIdentifier[]>(
      (acc, { fields, collapsed, id }) => (collapsed && fields.length ? [...acc, id] : acc),
      []
    );

    return removeChildrenOf(
      flattenedTree,
      activeId ? [activeId, ...collapsedItems] : collapsedItems
    );
  }, [activeId, filteredItems, items]);

  const sortableItemIds = useMemo(() => flattenedItems.map(({ id }) => id), [flattenedItems]);

  // Calculate projection for drag operations
  const projected = activeId && overId ? getProjection(flattenedItems, activeId, overId) : null;

  useEffect(() => {
    typingDone(() => {
      if (keywords) {
        const filteredItems = items.map(g => {
          const filteredFields = g.fields.filter(({ name }) =>
            name.toLowerCase().includes(keywords.toLowerCase())
          );
          return { ...g, fields: filteredFields };
        });
        setFilteredItems(filteredItems);
      } else {
        setFilteredItems(undefined);
      }
    });
  }, [items, keywords]);

  useEffect(() => {
    setItems(groups);
  }, [groups]);

  // Force remeasure when flattenedItems change (e.g., after filtering or collapse)
  useEffect(() => {
    if (listRef.current) {
      // Use setTimeout to ensure DOM has fully rendered before measuring
      setTimeout(() => {
        cache.clearAll();
        if (listRef.current) {
          listRef.current.recomputeRowHeights();
        }
      }, 100);
    }
  }, [flattenedItems.length, cache]);

  const handleCollapse = useCallback((id: string | number) => {
    setItems(items => {
      return setProperty(items, id, 'collapsed', value => !value);
    });
  }, []);

  const handleEdit = useCallback(
    (id: string | number, type: string) => {
      if (onEdit && typeof id === 'number') {
        onEdit(id, type);
      }
    },
    [onEdit]
  );

  // Handle brand expansion toggle
  const handleBrandExpansion = useCallback(
    (fieldId: string | number) => {
      // Find the index of the item to clear its cache
      const index = flattenedItems.findIndex(item => item.depth > 0 && item.id === fieldId);

      if (index !== -1) {
        // Clear the cache for this row and force re-measure
        cache.clear(index, 0);
        if (listRef.current) {
          listRef.current.recomputeRowHeights(index);
        }
      }
    },
    [cache, flattenedItems]
  );

  // Handle brand change and recalculate row height
  const handleBrandChange = useCallback(
    (customFieldId: number, brandIds: number[]) => {
      if (onBrandChange) {
        onBrandChange(customFieldId, brandIds);
      }

      // Find the index of the item to clear its cache
      const index = flattenedItems.findIndex(item => item.depth > 0 && item.id === customFieldId);

      if (index !== -1) {
        // Clear the cache for this row and force re-measure
        // Use setTimeout to ensure DOM updates have occurred
        setTimeout(() => {
          cache.clear(index, 0);
          if (listRef.current) {
            listRef.current.recomputeRowHeights(index);
          }
        }, 0);
      }
    },
    [cache, flattenedItems, onBrandChange]
  );

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id);
    setOverId(active.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverId(over?.id ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Reset state
    setActiveId(null);
    setOverId(null);

    if (projected && over) {
      const { depth, parentId } = projected;
      const clonedItems: FlattenedCustomFieldGroup[] = JSON.parse(
        JSON.stringify(flattenTree(items))
      );
      const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
      const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
      const activeTreeItem = clonedItems[activeIndex];

      clonedItems[activeIndex] = { ...activeTreeItem, depth, parentId };

      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
      const newItems = buildTree(sortedItems);

      setItems(newItems);

      // Notify parent of the change
      if (onGroupOrderChange) {
        onGroupOrderChange(newItems);
      }

      // Clear cache and recompute heights
      cache.clearAll();
      if (listRef.current) {
        listRef.current.recomputeRowHeights();
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };

  // Row renderer for virtual list
  const rowRenderer = useCallback(
    ({ index, key, parent, style }: any) => {
      const item = flattenedItems[index];
      const isGroup = item.depth === 0;

      const treeItem = (
        <ParentCustomFieldTreeItem
          key={item.id}
          value={item}
          depth={item.depth}
          collapsed={item.collapsed}
          brands={brands}
          brandOptions={brandOptions}
          isDraggable={isGroup ? item.id !== 'other' : true}
          hasPendingOrderChanges={hasPendingOrderChanges}
          onCollapse={isGroup ? () => handleCollapse(item.id) : undefined}
          onEdit={() => handleEdit(item.id, isGroup ? 'group' : 'field')}
          onBrandChange={!isGroup ? handleBrandChange : undefined}
          onBrandExpansionChange={!isGroup ? () => handleBrandExpansion(item.id) : undefined}
        />
      );

      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          {({ registerChild }: any) => (
            <div
              ref={node => {
                registerChild(node);
              }}
              style={style}
            >
              <SortableVirtualItem id={item.id}>{treeItem}</SortableVirtualItem>
            </div>
          )}
        </CellMeasurer>
      );
    },
    [
      flattenedItems,
      brands,
      brandOptions,
      hasPendingOrderChanges,
      cache,
      handleBrandChange,
      handleCollapse,
      handleEdit,
      handleBrandExpansion,
    ]
  );

  const overscanRowCount = 5;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sortableItemIds} strategy={verticalListSortingStrategy}>
        <AutoSizer>
          {({ width, height }) => (
            <List
              ref={listRef}
              width={width}
              height={height}
              rowCount={flattenedItems.length}
              rowHeight={cache.rowHeight}
              rowRenderer={rowRenderer}
              overscanRowCount={overscanRowCount}
              className="parent-custom-fields-virtual-list"
              deferredMeasurementCache={cache}
            />
          )}
        </AutoSizer>
      </SortableContext>

      {createPortal(
        <DragOverlay dropAnimation={dropAnimationConfig}>
          {activeId && (
            <SortableVirtualItem id={activeId}>
              <ParentCustomFieldTreeItem
                value={flattenedItems.find(({ id }) => id === activeId)}
                depth={projected ? projected.depth : 0}
                collapsed={false}
                brands={brands}
                brandOptions={brandOptions}
                isDraggable={false}
              />
            </SortableVirtualItem>
          )}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

export default React.memo(ParentCustomFieldsVirtualList);
